import { MemberResponseDto } from "@henophess/types";
import { normalizeMemberFromApiResponse } from "~/services/memberService";
import { Member } from "~/store";
import { GroupMember } from "~/types/group";

const API_BASE_URL = "/api";

export interface CreateMemberRequest {
  name: string;
  birthdate: string;
  gender: "M" | "F" | "O";
  email?: string;
  profileImageUrl?: string;
  groupId?: number;
  isUserMember?: boolean;
}

export type MemberResponse = MemberResponseDto & {
  member?: Member;
  existingMember?: Member;
};

export interface SupplementHistoryEntry {
  id: number;
  dosage: {
    id: number;
    supplement: string;
    amount?: number | null;
    unit?: string | null;
    frequency?: string | null;
    note?: string | null;
  };
  startDate: string;
  lastVerified?: string | null;
  ceasedDate?: string | null;
  notes?: string | null;
}

// Client version of SupplementHistoryResponseDto with the right types
export type SupplementHistoryResponse = {
  success: boolean;
  activeSupplements?: SupplementHistoryEntry[];
  ceasedSupplements?: SupplementHistoryEntry[];
  error?: string;
};

export interface RecommendationEntry {
  id: number;
  dosage: {
    id: number;
    supplement: string;
    amount?: number | null;
    unit?: string | null;
    frequency?: string | null;
    note?: string | null;
  };
  contextType: string;
  contextId: number;
  active: boolean;
  createdAt: string;
}

// Client version of RecommendationsResponseDto with the right types
export type RecommendationsResponse = {
  success: boolean;
  recommendations?: RecommendationEntry[];
  error?: string;
};

/**
 * Interface for member supplements response
 */
export interface MemberSupplementsResponse {
  recommendations: RecommendationEntry[];
  currentSupplements: SupplementHistoryEntry[];
  recentSupplements: SupplementHistoryEntry[];
}

/**
 * Fetch all members for the authenticated user
 * @returns Promise resolving to an array of members
 */
export const fetchMembers = async (): Promise<Member[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Failed to fetch members" }));
      throw new Error(errorData.message || "Failed to fetch members");
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching members:", error);
    return [];
  }
};

/**
 * Create a new member
 * @param member The member data to create
 * @returns Promise resolving to the created member
 */
export const createMember = async (member: CreateMemberRequest): Promise<MemberResponse> => {
  try {
    console.log("Sending create member request:", member);

    const response = await fetch(`${API_BASE_URL}/members`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(member),
      credentials: "include"
    });

    const data = await response.json();
    console.log("Raw API response for create member:", { status: response.status, data });

    // Important: The success field comes from the server response itself
    if (data.success) {
      console.log("Create member succeeded:", data);

      // Use our member normalization service to ensure proper conversion
      const normalizedMember = normalizeMemberFromApiResponse(data.data);

      return {
        success: true,
        member: normalizedMember
      };
    } else {
      // If server reports failure
      console.log("Create member failed with error:", data.error);
      return {
        success: false,
        error: data.error || "Failed to create member",
        existingMember: data.existingMember
      };
    }
  } catch (error) {
    console.error("Exception in createMember:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred creating member"
    };
  }
};

/**
 * Update an existing member
 * @param id The ID of the member to update
 * @param member The updated member data
 * @returns Promise resolving to the updated member
 */
export const updateMember = async (id: number, member: Partial<CreateMemberRequest>): Promise<MemberResponse> => {
  try {
    console.log("Sending update member request:", { id, member });

    const response = await fetch(`${API_BASE_URL}/members/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(member),
      credentials: "include"
    });

    const data = await response.json();
    console.log("Raw API response for update member:", { status: response.status, data });

    // Important: The success field comes from the server response itself
    if (data.success) {
      console.log("Update member succeeded:", data);

      // Use our member normalization service to ensure proper conversion
      const normalizedMember = normalizeMemberFromApiResponse(data.data);

      return {
        success: true,
        member: normalizedMember
      };
    } else {
      // If server reports failure
      console.log("Update member failed with error:", data.error);
      return {
        success: false,
        error: data.error || "Failed to update member",
        existingMember: data.existingMember
      };
    }
  } catch (error) {
    console.error("Exception in updateMember:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred updating member"
    };
  }
};

/**
 * Delete a member
 * @param id The ID of the member to delete
 * @returns Promise resolving to a success status
 */
export const deleteMember = async (id: number): Promise<MemberResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/${id}`, {
      method: "DELETE",
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Failed to delete member" }));
      return {
        success: false,
        error: errorData.message || "Failed to delete member"
      };
    }

    return {
      success: true,
      message: "Member deleted successfully"
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred deleting member"
    };
  }
};

/**
 * Fetch supplement history for a member
 * @param memberId The ID of the member to fetch supplement history for
 * @returns Promise resolving to a SupplementHistoryResponse
 */
export const fetchMemberSupplementHistory = async (memberId: number): Promise<SupplementHistoryResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/${memberId}/supplements`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || "Failed to fetch supplement history"
      };
    }

    return {
      success: true,
      activeSupplements: data.activeSupplements || [],
      ceasedSupplements: data.ceasedSupplements || []
    };
  } catch (error) {
    console.error("Error fetching supplement history:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred fetching supplement history"
    };
  }
};

/**
 * Add a supplement history entry
 * @param groupMemberId The ID of the group member
 * @param supplementData The supplement data including type, dosage, etc.
 * @returns Promise resolving to a success/error response
 */
export const addSupplementHistory = async (
  groupMemberId: number,
  supplementData: {
    supplement: string;
    amount?: number;
    dosageUnit?: string;
    frequency?: string;
    note?: string;
  }
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/supplement-history`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({
        groupMemberId,
        supplementData
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || "Failed to add supplement"
      };
    }

    return {
      success: true,
      message: data.message || "Supplement added successfully"
    };
  } catch (error) {
    console.error("Error adding supplement:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred adding the supplement"
    };
  }
};

/**
 * Mark a supplement as ceased (no longer taking)
 * @param groupMemberId The ID of the group member
 * @param supplementType The type of supplement to cease
 * @returns Promise resolving to a success/error response
 */
export const ceaseSupplementHistory = async (
  groupMemberId: number,
  supplementType: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/supplements/${supplementType}/cease`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({
        groupMemberId,
        ceasedDate: new Date().toISOString().split("T")[0] // Send today's date in YYYY-MM-DD format
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || "Failed to update supplement status"
      };
    }

    return {
      success: true
    };
  } catch (error) {
    console.error("Error ceasing supplement:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred updating supplement status"
    };
  }
};

/**
 * Fetch recommendations for a group member
 * @param groupMemberId The group_members_id to fetch recommendations for
 * @returns Promise resolving to a RecommendationsResponse
 */
export const fetchMemberRecommendations = async (groupMemberId: number): Promise<RecommendationsResponse> => {
  try {
    const endpoint = `${API_BASE_URL}/group-members/${groupMemberId}/recommendations`;

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || "Failed to fetch recommendations"
      };
    }

    return {
      success: true,
      recommendations: data.recommendations || []
    };
  } catch (error) {
    console.error("Error fetching recommendations:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred fetching recommendations"
    };
  }
};

/**
 * Fetch all members in the user's default group
 * @returns Promise resolving to an array of members with their group membership information
 */
export const fetchDefaultGroupMembers = async (): Promise<
  {
    member: Member;
    groupMembership: {
      role: string;
      joined_at: string;
      added_by_user_id: number;
    };
  }[]
> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/default-group`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Failed to fetch default group members" }));
      throw new Error(errorData.message || "Failed to fetch default group members");
    }

    const result = await response.json();

    // Return empty array if unsuccessful or no data
    if (!result.success || !result.data) {
      console.error("Error fetching default group members:", result.error || "No data returned");
      return [];
    }

    return result.data;
  } catch (error) {
    console.error("Error fetching default group members:", error);
    return [];
  }
};

/**
 * Fetch the group_members_id for a specific member
 * This function will automatically use the member's default group
 * @param memberId The ID of the member
 * @returns Promise resolving to the group_members_id or null if not found
 */
export const fetchGroupMembershipId = async (memberId: number): Promise<number | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/${memberId}/group-membership`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Failed to fetch group membership ID" }));
      throw new Error(errorData.message || "Failed to fetch group membership ID");
    }

    const result = await response.json();

    // Return null if unsuccessful or no data
    if (!result.success || !result.data) {
      console.error("Error fetching group membership ID:", result.error || "No data returned");
      return null;
    }

    return result.data.groupMembershipId;
  } catch (error) {
    console.error("Error fetching group membership ID:", error);
    return null;
  }
};

/**
 * Fetch the complete group member details for a specific member in a specific group
 * @param memberId The ID of the member
 * @param groupId The ID of the group
 * @returns Promise resolving to the GroupMember object or null if not found
 */
export const fetchGroupMemberDetails = async (memberId: number, groupId: number): Promise<GroupMember | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/${memberId}/group-member/${groupId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Failed to fetch group member details" }));
      throw new Error(errorData.message || "Failed to fetch group member details");
    }

    const result = await response.json();

    // Return null if unsuccessful or no data
    if (!result.success || !result.data) {
      console.error("Error fetching group member details:", result.error || "No data returned");
      return null;
    }

    return result.data.groupMember;
  } catch (error) {
    console.error("Error fetching group member details:", error);
    return null;
  }
};

/**
 * Fetch supplement history for a group member by group_members_id
 * @param groupMemberId The ID of the group member to fetch supplement history for
 * @returns Promise resolving to a SupplementHistoryResponse
 */
export const fetchGroupMemberSupplements = async (groupMemberId: number): Promise<SupplementHistoryResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/group-members/${groupMemberId}/supplements`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || "Failed to fetch supplement history"
      };
    }

    return {
      success: true,
      activeSupplements: data.activeSupplements || [],
      ceasedSupplements: data.ceasedSupplements || []
    };
  } catch (error) {
    console.error("Error fetching supplement history:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An error occurred fetching supplement history"
    };
  }
};

/**
 * Fetch supplements (recommendations and current/recent supplements) for a group member
 * @param groupMemberId The group member ID
 * @returns Promise resolving to member supplements data
 */
export const fetchMemberSupplements = async (groupMemberId: number): Promise<MemberSupplementsResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/members/${groupMemberId}/all-supplements`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    // If we get a 404, it means the member exists but has no supplements
    // Return an empty object with the correct structure instead of null
    if (response.status === 404) {
      console.log(`No supplements found for group member ID ${groupMemberId}, returning empty data`);
      return {
        recommendations: [],
        currentSupplements: [],
        recentSupplements: []
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Failed to fetch member supplements" }));
      console.error("Error fetching member supplements:", errorData);
      // Return empty arrays instead of null for error cases
      return {
        recommendations: [],
        currentSupplements: [],
        recentSupplements: []
      };
    }

    return await response.json();
  } catch (error) {
    console.error("Exception in fetchMemberSupplements:", error);
    // Return empty arrays instead of null for any error
    return {
      recommendations: [],
      currentSupplements: [],
      recentSupplements: []
    };
  }
};
